import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  input,
  output,
  inject,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import {
  ClientTrialService,
  TimeconversionService,
  TrialService,
  ClientService,
  ToastMessageService,
} from '@fitness-force/api';
import { Status } from '@fitness-force/enum';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type { CreateClientTrial, TrialMaster } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { AssignedToComponent } from '../staff/assigned-to/assigned-to.component';
import {
  Subscription,
  of,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  combineLatest,
  Subject,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeatureFlagService } from '@fitness-force/shared-utils';

@Component({
  selector: 'create-client-trial',
  templateUrl: './create-client-trial.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    DialogModule,
    DatePickerModule,
    SkeletonModule,
    PopoverModule,
    StyleClassModule,
    ErrorMsgComponent,
    AssignedToComponent,
    TranslateModule,
    InputTextModule,
    DatePipe,
    NgClass,
  ],
})
export class CreateClientTrialComponent implements OnInit, OnDestroy {
  private readonly clientTrialService = inject(ClientTrialService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly trialMasterService = inject(TrialService);
  translate = inject(TranslateService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly timeService = inject(TimeconversionService);
  private readonly clientService = inject(ClientService);
  private readonly featureFlagService = inject(FeatureFlagService);

  readonly tenantId = input<number>(0);
  readonly clientId = input<number>(0);
  readonly clientKeyfob = input<string>('', { alias: 'keyfob' });
  readonly visible = input<boolean>(false);

  readonly closeEvent = output<any>({ alias: 'closeEvent' });

  trialMaster: TrialMaster | undefined;
  minimumDate = new Date();
  clientTrialForm: UntypedFormGroup = new UntypedFormGroup({});
  showDaysBased: boolean = false;
  showClassBased: boolean = false;

  isFormSubmitting = false;

  trialEndDate = new Date();

  // Keyfob related properties
  digitalKeyFOBSelected: boolean = false;
  physicalKeyFOBSelected: boolean = true;
  noKeyFOBSelected: boolean = false;
  keyFobRequired: boolean = true;
  validAlphaNumericPattern = '^[a-zA-Z0-9]*$';
  isValidatingKeyfob: boolean = false;
  isKeyfobValid: boolean = false;
  keyfobValidationMessage: string = '';
  showDigitalKeyFob: boolean = false;
  private keyfobValidationSubscription!: Subscription;
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Check all feature flags using combineLatest for cleaner code
    // This waits for Statsig initialization and updates all flags at once
    combineLatest([
      this.featureFlagService.getFlag$('keyfob'),
      this.featureFlagService.getFlag$('show_digital_keyfob'),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([keyfobEnabled, showDigitalKeyfob]) => {
        this.keyFobRequired = keyfobEnabled;
        this.showDigitalKeyFob = showDigitalKeyfob;

        if (!showDigitalKeyfob) {
          this.physicalKeyFOBSelected = true;
          this.digitalKeyFOBSelected = false;
          this.noKeyFOBSelected = false;
        }

        // Rebuild form with updated validators
        this.buildForm();
        this.cd.detectChanges();
      });

    this.getTrialMaster();
  }

  private buildForm(): void {
    // Build validators array for keyfob, filtering out null values
    const keyfobValidators = [
      this.keyFobRequired ? Validators.required : null,
      Validators.pattern(this.validAlphaNumericPattern),
    ].filter((validator) => validator !== null);

    this.clientTrialForm = this.fb.group({
      startDate: [this.minimumDate, Validators.required],
      trialOwner: [null, Validators.required],
      keyfob: [this.clientKeyfob(), keyfobValidators],
    });

    // Set up keyfob validation listener only if keyfob is required
    if (this.keyFobRequired) {
      this.setupKeyfobValidation();
    }
  }

  ngOnDestroy(): void {
    if (this.keyfobValidationSubscription) {
      this.keyfobValidationSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupKeyfobValidation(): void {
    if (!this.keyFobFormControl) {
      return; // Exit early if form control doesn't exist yet
    }

    this.keyfobValidationSubscription = this.keyFobFormControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value: string) => {
          if (
            value?.trim() &&
            value !== this.clientKeyfob() &&
            this.tenantId() > 0
          ) {
            this.isValidatingKeyfob = true;
            this.keyfobValidationMessage = '';
            this.cd.detectChanges();
            return this.clientService
              .validateKeyfob(this.tenantId(), value.trim(), this.clientId())
              .pipe(
                catchError((error: any) => {
                  this.isValidatingKeyfob = false;
                  this.isKeyfobValid = false;
                  if (error?.originalError?.error?.errorMessage) {
                    this.keyfobValidationMessage =
                      error.originalError.error.errorMessage;
                  } else {
                    this.keyfobValidationMessage = 'Invalid keyfob number';
                  }
                  this.cd.detectChanges();
                  return of(null);
                }),
              );
          } else {
            this.isValidatingKeyfob = false;
            this.isKeyfobValid = false;
            this.keyfobValidationMessage = '';
            this.cd.detectChanges();
            return of(null);
          }
        }),
      )
      .subscribe((response) => {
        if (response !== null) {
          this.isValidatingKeyfob = false;
          this.isKeyfobValid = true;
          this.keyfobValidationMessage = 'Keyfob is valid';
          this.cd.detectChanges();
        }
      });
  }

  toggleKeyFobSelectionType(
    digitalKeyFOBSelected: boolean,
    physicalKeyFOBSelected: boolean,
  ): void {
    this.digitalKeyFOBSelected = digitalKeyFOBSelected;
    this.physicalKeyFOBSelected = physicalKeyFOBSelected;
    if (!this.digitalKeyFOBSelected && !this.physicalKeyFOBSelected) {
      this.noKeyFOBSelected = true;
    } else {
      this.noKeyFOBSelected = false;
    }

    if (!this.keyFobFormControl) {
      return; // Exit early if form control doesn't exist yet
    }

    if (this.physicalKeyFOBSelected) {
      this.keyFobFormControl.addValidators(Validators.required);
      this.keyFobFormControl.addValidators(
        Validators.pattern(this.validAlphaNumericPattern),
      );
    } else {
      this.keyFobFormControl.removeValidators(Validators.required);
      this.keyFobFormControl.removeValidators(
        Validators.pattern(this.validAlphaNumericPattern),
      );
      this.keyFobFormControl.clearValidators();
      this.keyFobFormControl.setErrors(null);
    }
    this.keyFobFormControl.updateValueAndValidity();
    this.cd.detectChanges();
  }

  getTrialMaster() {
    this.trialMasterService.getTrials(this.tenantId()).subscribe({
      next: (response: any) => {
        if (response?.items) {
          const trialMasters = response.items as TrialMaster[];
          this.trialMaster = trialMasters.find(
            (o) =>
              o.status == Status.Active ||
              o.status.toString() == Status[Status.Active],
          );
          this.trialEndDate = new Date(
            this.trialEndDate.setDate(
              this.trialEndDate.getDate() +
                this.trialMaster!.max_trial_validity -
                1,
            ),
          );
          this.showDaysBased = true;
        }
      },
      error: (error) => {
        this.handleError(error);
        this.trialMaster = undefined;
      },
    });
  }

  saveTrialForm() {
    if (this.clientTrialForm.valid) {
      // Check keyfob validation
      if (
        this.keyFobRequired &&
        this.physicalKeyFOBSelected &&
        this.keyFobFormControl?.invalid
      ) {
        this.keyFobFormControl?.markAsDirty();
        this.cd.detectChanges();
        return;
      }

      const data = { ...this.clientTrialForm.getRawValue() };
      const createClientTrial: CreateClientTrial & {
        keyfob_type?: string;
        keyfob_number?: string | null;
      } = {} as CreateClientTrial & {
        keyfob_type?: string;
        keyfob_number?: string | null;
      };
      const startDateTime = new Date(
        new Date(data.startDate).setHours(0, 0, 0, 0),
      );
      createClientTrial.start_date =
        this.timeService.ConvertLocalDateTimeToUTC(startDateTime);
      createClientTrial.opportunity_id = 0;
      createClientTrial.owner_id = data.trialOwner.id;
      createClientTrial.trial_master_id = this.trialMaster!.id;

      // Add keyfob data to payload
      if (this.digitalKeyFOBSelected) {
        createClientTrial.is_digital_keyfob = true;
        createClientTrial.keyfob_number = '';
      } else if (this.physicalKeyFOBSelected && data.keyfob) {
        createClientTrial.is_digital_keyfob = false;
        createClientTrial.keyfob_number = data.keyfob;
      } else {
        createClientTrial.is_digital_keyfob = false;
        createClientTrial.keyfob_number = '';
      }

      this.isFormSubmitting = true;
      this.clientTrialService
        .CreateClientTrial(this.tenantId(), this.clientId(), createClientTrial)
        .subscribe({
          next: () => {
            this.isFormSubmitting = false;
            this.translate
              .get('CLIENT_TRIAL.MSG_SUCCESS')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.closePopup(true);
          },
          error: (error: any) => {
            this.isFormSubmitting = false;
            this.handleError(error);
          },
        });
    } else {
      // Todo Fire all control touches ...
      Object.keys(this.clientTrialForm.controls).forEach((field) => {
        const control = this.clientTrialForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.cd.detectChanges();
    }
  }

  handleError(error: any) {
    if (error.originalError.error.length > 0) {
      error.originalError.error.forEach((e: any) => {
        if (e.errorCode.toLowerCase() == 'overlap') {
          this.showOverlapErrorMessage(e);
        } else {
          this.toastMessageService.showErrorMessage(e);
        }
      });
    } else {
      this.toastMessageService.showErrorMessage(error);
    }
  }

  showOverlapErrorMessage(error: any) {
    this.translate
      .get('SCHEDULE.MSG.OVERLAP_MESSAGE', {
        type: error.jsonObject.identifier,
        startDate: this.timeService.formatDate(
          this.timeService.ConvertUTCToLocalDateTime(
            error.jsonObject.startDateTime,
          ),
          'medium',
        ),
        endDate: this.timeService.formatDate(
          this.timeService.ConvertUTCToLocalDateTime(
            error.jsonObject.endDateTime,
          ),
          'medium',
        ),
      })
      .subscribe((translation) => {
        this.toastMessageService.showErrorMessage(translation);
      });
  }

  getEndDate(event: any) {
    const selectedDate = new Date(this.StartDateFormControl.value);
    this.trialEndDate = new Date(
      selectedDate.setDate(
        selectedDate.getDate() + this.trialMaster!.max_trial_validity - 1,
      ),
    );
  }

  closePopup(reloadData: boolean) {
    this.closeEvent.emit({ closeRoomPopup: true, reloadData: false });
    this.showDaysBased = false;
    this.showClassBased = false;
    this.clientTrialForm.reset();
    this.closeEvent.emit({ closeRoomPopup: true, reloadData: reloadData });
  }

  get StartDateFormControl(): UntypedFormControl {
    return this.clientTrialForm?.controls?.startDate as UntypedFormControl;
  }

  get trialOwnerFormControl(): UntypedFormControl {
    return this.clientTrialForm?.controls?.trialOwner as UntypedFormControl;
  }

  get keyFobFormControl(): UntypedFormControl {
    return this.clientTrialForm?.controls?.keyfob as UntypedFormControl;
  }

  onTrainerChange(event: any): void {
    // Handle trainer change if needed
  }

  onDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
